"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useDemoOrgMode } from "./demo-org-mode-context";
import type { FeatureTab } from "../types";
import { visibleDemoTabs } from "../lib/demo-tabs";

interface FeatureTabsProps {
  activeTab: FeatureTab;
  onTabChange: (tab: FeatureTab) => void;
  className?: string;
}

export function FeatureTabs({ activeTab, onTabChange, className }: FeatureTabsProps) {
  const { isCollab } = useDemoOrgMode();
  const tabs = visibleDemoTabs(isCollab);

  return (
    <div
      className={cn(
        "grid w-full max-w-[640px] grid-cols-2 gap-2 sm:flex sm:max-w-none sm:flex-wrap sm:items-center sm:justify-center",
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              "relative rounded-full px-3 py-2 text-xs font-semibold transition-all duration-200 sm:px-5 sm:py-2.5 sm:text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tami-green",
              isActive
                ? "text-tami-dark"
                : "text-white/60 hover:text-white/90",
            )}
            aria-pressed={isActive}
          >
            {isActive && (
              <motion.span
                layoutId="feature-tab-bg"
                className="absolute inset-0 rounded-full bg-tami-green"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative flex items-center gap-1.5">
              <span aria-hidden="true">{tab.icon}</span>
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
